/**
 * GraphQL Queries for Dashboard Data
 * 
 * Real queries to fetch dashboard data from the compressed backend
 */

import { gql } from '@apollo/client';

// Critical Workflow Queries
export const GET_CRITICALITY_CONFIG = gql`
  query GetCriticalityConfig($workflowType: String!, $tenantId: String!) {
    getCriticalityConfig(workflowType: $workflowType, tenantId: $tenantId) {
      ruleId
      appliesToTypes
      deduplicationFields
      duplicateWindowMinutes
      criticalityLevel
      requiresExclusiveLock
      requiredWitnesses
      offlineCanRun
    }
  }
`;

export const LIST_CRITICAL_WORKFLOW_TYPES = gql`
  query ListCriticalWorkflowTypes($tenantId: String!) {
    listCriticalWorkflowTypes(tenantId: $tenantId)
  }
`;

export const CHECK_WORKFLOW_DUPLICATES = gql`
  query CheckWorkflowDuplicates($workflowExecution: WorkflowExecutionInput!, $lookbackMinutes: Int!) {
    checkWorkflowDuplicates(workflowExecution: $workflowExecution, lookbackMinutes: $lookbackMinutes) {
      workflowId
      similarityScore
      timeDifferenceMinutes
      matchingFields
    }
  }
`;

export const GET_WORKFLOW_EXECUTION_HISTORY = gql`
  query GetWorkflowExecutionHistory($workflowType: String!, $tenantId: String!, $lookbackDays: Int!) {
    getWorkflowExecutionHistory(workflowType: $workflowType, tenantId: $tenantId, lookbackDays: $lookbackDays)
  }
`;

// Regulatory Compliance Queries - LIFE-CRITICAL for legal compliance
export const GET_REGULATORY_COMPLIANCE_CONFIG = gql`
  query GetRegulatoryComplianceConfig($tenant: String!, $configType: String!) {
    getRegulatoryComplianceConfig(tenant: $tenant, configType: $configType)
  }
`;

export const LIST_REGULATORY_COMPLIANCE_CONFIGS = gql`
  query ListRegulatoryComplianceConfigs($tenant: String!) {
    listRegulatoryComplianceConfigs(tenant: $tenant) {
      id
      tenant
      configType
      configData
    }
  }
`;

export const FIND_METADATA = gql`
  query FindMetadata($tenant: String!, $metadataType: String!, $key: String, $isActive: Boolean) {
    findMetadata(tenant: $tenant, metadataType: $metadataType, key: $key, isActive: $isActive) {
      id
      metadataType
      key
      data
      isActive
    }
  }
`;

// Threshold access APIs
export const GET_REGULATORY_THRESHOLD = gql`
  query GetRegulatoryThreshold($tenant: String!, $thresholdType: String!, $frameworkId: String!) {
    getRegulatoryThreshold(tenant: $tenant, thresholdType: $thresholdType, frameworkId: $frameworkId) {
      id
      tenant
      thresholdType
      frameworkId
      numericValue
      unit
      confidenceScore
      effectiveDate
      sourceAuthority
      metadata
    }
  }
`;

export const GET_REGULATORY_THRESHOLDS_BATCH = gql`
  query GetRegulatoryThresholdsBatch($tenant: String!, $inputs: [RegulatoryThresholdQueryInput!]!) {
    getRegulatoryThresholdsBatch(tenant: $tenant, inputs: $inputs) {
      id
      tenant
      thresholdType
      frameworkId
      numericValue
      unit
    }
  }
`;

// Regulatory Compliance Mutations - LIFE-CRITICAL for configuration management
export const STORE_REGULATORY_COMPLIANCE_CONFIG = gql`
  mutation StoreRegulatoryComplianceConfig($tenant: String!, $configType: String!, $configData: JSON!) {
    storeRegulatoryComplianceConfig(tenant: $tenant, configType: $configType, configData: $configData)
  }
`;

export const UPDATE_REGULATORY_COMPLIANCE_CONFIG = gql`
  mutation UpdateRegulatoryComplianceConfig($tenant: String!, $configId: String!, $configData: JSON!) {
    updateRegulatoryComplianceConfig(tenant: $tenant, configId: $configId, configData: $configData)
  }
`;

export const UPDATE_REGULATORY_THRESHOLD = gql`
  mutation UpdateRegulatoryThreshold($input: UpdateRegulatoryThresholdInput!) {
    updateRegulatoryThreshold(input: $input)
  }
`;

export const EMERGENCY_OVERRIDE_REGULATORY_THRESHOLD = gql`
  mutation EmergencyOverrideRegulatoryThreshold($input: EmergencyOverrideInput!) {
    emergencyOverrideRegulatoryThreshold(input: $input)
  }
`;

// Dashboard Data Query - fetches all dashboard data in one request
export const GET_DASHBOARD_DATA = gql`
  query GetDashboardData {
    # Workflow overview metrics - using Instance data
    instances: listDrafts(input: { tenant: "test", kind: "Instance" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Active jobs - using JobStatus data
    jobs: listDrafts(input: { tenant: "test", kind: "JobStatus" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # System activity - using TimelineItem data
    activity: listDrafts(input: { tenant: "test", kind: "TimelineItem" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Individual queries for specific widgets
export const GET_WORKFLOW_OVERVIEW = gql`
  query GetWorkflowOverview($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Instance" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    # Get full draft data for each workflow instance to populate Tabulator fields
    instanceDrafts: listDrafts(input: { tenant: $tenant, kind: "Instance" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_WORKFLOW_DEFINITIONS = gql`
  query GetWorkflowDefinitions($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_ACTIVE_JOBS = gql`
  query GetActiveJobs($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "JobStatus" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_RECENT_ACTIVITY = gql`
  query GetRecentActivity($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "TimelineItem" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_SYSTEM_HEALTH = gql`
  query GetSystemHealth($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_PERFORMANCE_METRICS = gql`
  query GetPerformanceMetrics($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "JobStatus" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Governance & Operations Types - Required for Production
export const GET_POLICIES = gql`
  query GetPolicies($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Policy" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_CONTEXTS = gql`
  query GetContexts($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Context" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_SCHEDULES = gql`
  query GetSchedules($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Schedule" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_RESOURCES = gql`
  query GetResources($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Resource" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_INTEGRATIONS = gql`
  query GetIntegrations($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Integration" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_ESCALATIONS = gql`
  query GetEscalations($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Escalation" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Regulatory Authority Queries - Federation-enabled
export const GET_AUTHORITIES = gql`
  query GetAuthorities($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Authority" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_REGULATIONS = gql`
  query GetRegulations($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Regulation" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Federated query to get WorkflowDefinition with Authority details
export const GET_WORKFLOW_DEFINITION_WITH_AUTHORITY = gql`
  query GetWorkflowDefinitionWithAuthority($tenant: String!, $key: String!) {
    workflowDefinition: getDraft(input: { tenant: $tenant, kind: "WorkflowDefinition", key: $key }) {
      key
      draft
    }
    # Federation will resolve the authority based on authorityId in the draft
    authority: getDraft(input: { tenant: $tenant, kind: "Authority", key: $key }) @skip(if: true) {
      key
      draft
    }
  }
`;

// Federated query to get all regulatory data for a workflow
export const GET_REGULATORY_FRAMEWORK = gql`
  query GetRegulatoryFramework($tenant: String!, $workflowKey: String!) {
    workflowDefinition: getDraft(input: { tenant: $tenant, kind: "WorkflowDefinition", key: $workflowKey }) {
      key
      draft
    }
    
    # Get all authorities (will be filtered client-side)
    authorities: listDrafts(input: { tenant: $tenant, kind: "Authority" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Get all regulations (will be filtered client-side) 
    regulations: listDrafts(input: { tenant: $tenant, kind: "Regulation" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Get compliance policies related to this workflow
    policies: listDrafts(input: { tenant: $tenant, kind: "Policy" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Authority hierarchy query - gets parent/child relationships
export const GET_AUTHORITY_HIERARCHY = gql`
  query GetAuthorityHierarchy($tenant: String!, $rootAuthorityId: String!) {
    authorities: listDrafts(input: { tenant: $tenant, kind: "Authority" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Regulation impact analysis - find all workflows affected by a regulation
export const GET_REGULATION_IMPACT = gql`
  query GetRegulationImpact($tenant: String!, $regulationId: String!) {
    regulation: getDraft(input: { tenant: $tenant, kind: "Regulation", key: $regulationId }) {
      key
      draft
    }
    
    # Get all workflow definitions to analyze impact client-side
    workflowDefinitions: listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Get related authorities
    authorities: listDrafts(input: { tenant: $tenant, kind: "Authority" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Compliance dashboard query - comprehensive regulatory status
export const GET_COMPLIANCE_DASHBOARD = gql`
  query GetComplianceDashboard($tenant: String!) {
    # All workflow definitions with regulatory requirements
    workflowDefinitions: listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # All applicable authorities
    authorities: listDrafts(input: { tenant: $tenant, kind: "Authority" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # All applicable regulations
    regulations: listDrafts(input: { tenant: $tenant, kind: "Regulation" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Related compliance policies
    policies: listDrafts(input: { tenant: $tenant, kind: "Policy" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Active instances for compliance monitoring
    instances: listDrafts(input: { tenant: $tenant, kind: "Instance" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
    
    # Audit and escalation data
    escalations: listDrafts(input: { tenant: $tenant, kind: "Escalation" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Get individual draft data
export const GET_DRAFT = gql`
  query GetDraft($input: GetDraftInput!) {
    getDraft(input: $input) {
      key
      draft
    }
  }
`;

// Fragment for draft list items
export const DRAFT_ITEM_FRAGMENT = gql`
  fragment DraftItem on DraftListItem {
    key
    updatedBy
    updatedMs
    deleted
  }
`;

// Fragment for full draft data
export const DRAFT_DATA_FRAGMENT = gql`
  fragment DraftData on Draft {
    key
    draft
  }
`;

// Input type definitions (for TypeScript)
export interface ListDraftsInput {
  tenant: string;
  kind: string;
}

export interface GetDraftInput {
  tenant: string;
  kind: string;
  key: string;
}

export interface DraftListItem {
  key: string;
  owner: string;
  updatedBy: string;
  updatedMs: number;
  deleted?: boolean;
}

export interface Draft {
  key: string;
  draft: Record<string, unknown>; // JSON data
}

// Missing query exports for TS2305 compliance
export const GET_TASKS = gql`
  query GetTasks($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "Task" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_SIGNALS = gql`
  query GetSignals($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "SignalTemplate" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_ACTOR_DESCRIPTORS = gql`
  query GetActorDescriptors($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "ActorDescriptor" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_TIMELINE_ITEMS = gql`
  query GetTimelineItems($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "TimelineItem" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_TRANSITION_CATALOGS = gql`
  query GetTransitionCatalogs($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "TransitionCatalog" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_JOB_STATUSES = gql`
  query GetJobStatuses($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "JobStatus" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

export const GET_USER_WORKFLOWS = gql`
  query GetUserWorkflows($tenant: String!) {
    listDrafts(input: { tenant: $tenant, kind: "WorkflowDefinition" }) {
      key
      owner
      updatedBy
      updatedMs
      deleted
    }
  }
`;

// Universal Domain Renderer Queries
export const GET_DOMAIN_CONFIGURATION = gql`
  query GetDomainConfiguration($domain: String!, $tenant: String!) {
    getDomainConfiguration(domain: $domain, tenant: $tenant) {
      domain
      enabled_primitives
      circuit_breakers {
        max_primitives_per_domain
        emergency_fallback_enabled
        load_threshold
        performance_threshold_ms
      }
      compliance_frameworks
      safety_score
      metadata_version
    }
  }
`;

export const LOAD_PRIMITIVES = gql`
  query LoadPrimitives($domain: String!, $tenant: String!, $enabledPrimitives: [String!]!) {
    loadPrimitives(domain: $domain, tenant: $tenant, enabledPrimitives: $enabledPrimitives) {
      primitiveType
      configuration
      metadata
      constraints
      circuitBreakerState {
        state
        failureCount
        lastFailureTime
      }
    }
  }
`;

export const GET_ALL_DOMAINS = gql`
  query GetAllDomains($tenant: String!) {
    getAllDomains(tenant: $tenant) {
      domain
      enabledPrimitives
      complexityLevel
      safetyScore
      lastUpdated
    }
  }
`;

// Circuit Breaker Safety Integration Queries
export const GET_CIRCUIT_BREAKER_SAFETY_METRICS = gql`
  query GetCircuitBreakerSafetyMetrics($tenant: String!) {
    getCircuitBreakerSafetyMetrics(tenant: $tenant) {
      circuit_breaker_states {
        name
        state
        failure_count
        last_failure_time
        safety_impact_score
      }
      overall_safety_score
      emergency_mode_active
      compliance_frameworks_affected
      load_shedding_active
      fallback_primitives_active
    }
  }
`;


// Domain Configuration Management Queries
export const GET_DOMAIN_CONFIGURATIONS = gql`
  query GetDomainConfigurations($tenant: String!) {
    getDomainConfigurations(tenant: $tenant) {
      domain
      enabled_primitives
      circuit_breakers {
        max_primitives_per_domain
        emergency_fallback_enabled
        load_threshold
        performance_threshold_ms
      }
      compliance_frameworks
      safety_score
      metadata_version
      created_at
      updated_at
      created_by
    }
  }
`;

export const VALIDATE_DOMAIN_CONFIGURATION = gql`
  query ValidateDomainConfiguration($input: DomainConfigurationInput!) {
    validateDomainConfiguration(input: $input) {
      is_valid
      validation_errors
      safety_score_impact
      compliance_warnings
      performance_impact
    }
  }
`;

// Safety Validation Queries
export const VALIDATE_SAFETY_PRESERVATION = gql`
  query ValidateSafetyPreservation($tenant: String!) {
    validateSafetyPreservation(tenant: $tenant) {
      overall_safety_score
      domain_safety_scores {
        domain
        score
        violations
        critical_issues
      }
      emergency_protocols {
        protocol_id
        status
        last_tested
        response_time_ms
      }
      audit_trail_integrity {
        total_entries
        verified_entries
        integrity_percentage
        tamper_attempts
      }
      circuit_breaker_safety_correlation {
        correlation_score
        safety_impact_events
        recommendations
      }
      compliance_framework_status {
        framework
        status
        violations
        last_validation
      }
    }
  }
`;

// Comprehensive Compliance Validation
export const VALIDATE_COMPREHENSIVE_COMPLIANCE = gql`
  query ValidateComprehensiveCompliance($tenant: String!) {
    validateComprehensiveCompliance(tenant: $tenant) {
      hipaa_compliance {
        status
        phi_protection_score
        access_controls_valid
        audit_trail_compliant
        encryption_compliant
        violations
        last_assessment
      }
      sox_compliance {
        status
        financial_integrity_score
        segregation_of_duties_valid
        change_management_compliant
        audit_trail_compliant
        violations
        last_assessment
      }
      gdpr_compliance {
        status
        data_protection_score
        consent_management_valid
        data_portability_compliant
        right_to_deletion_compliant
        violations
        last_assessment
      }
      pci_dss_compliance {
        status
        payment_security_score
        cardholder_data_protected
        secure_network_compliant
        vulnerability_management_compliant
        violations
        last_assessment
      }
      overall_compliance_score
      critical_violations
      compliance_drift_detection
      next_audit_due
    }
  }
`;

export const EXPORT_COMPLIANCE_REPORT = gql`
  query ExportComplianceReport($tenant: String!, $frameworks: [String!]!, $format: String!) {
    exportComplianceReport(tenant: $tenant, frameworks: $frameworks, format: $format) {
      report_url
      expires_at
      file_size_bytes
    }
  }
`;

// Performance Testing Queries
export const GET_PERFORMANCE_TEST_RESULTS = gql`
  query GetPerformanceTestResults($testId: String!) {
    getPerformanceTestResults(testId: $testId) {
      test_id
      status
      configuration
      results {
        timestamp
        primitive_load
        response_time_ms
        circuit_breaker_state
        safety_score
        error_rate
        throughput_rps
      }
      summary {
        total_requests
        successful_requests
        failed_requests
        avg_response_time_ms
        p95_response_time_ms
        p99_response_time_ms
        circuit_breaker_trips
        safety_score_impact
      }
      circuit_breaker_analysis {
        threshold_validation
        recovery_time_ms
        fallback_effectiveness
        safety_preservation
      }
      recommendations
    }
  }
`;

export const GET_CIRCUIT_BREAKER_METRICS = gql`
  query GetCircuitBreakerMetrics($tenant: String!) {
    getCircuitBreakerMetrics(tenant: $tenant) {
      current_state
      failure_count
      success_count
      avg_response_time_ms
      throughput_rps
      error_rate
      last_failure_time
      circuit_breaker_states {
        name
        state
        failure_count
        last_trip_time
      }
    }
  }
`;

// Dynamic Filter Configuration Queries (Backend-Driven UI Configuration)
export const GET_AVAILABLE_COMPLIANCE_FRAMEWORKS = gql`
  query GetAvailableComplianceFrameworks($tenant: String!) {
    getAvailableComplianceFrameworks(tenant: $tenant) {
      frameworkId
      name
      description
      category
      regulatoryAuthority
      applicableDomains
      severityLevel
    }
  }
`;

export const GET_FILTER_CONFIGURATIONS = gql`
  query GetFilterConfigurations($tenant: String!, $component: String!) {
    getFilterConfigurations(tenant: $tenant, component: $component) {
      fieldName
      filterType
      availableValues
      defaultValues
      isSearchable
      isMultiSelect
      category
      priority
    }
  }
`;

export const GET_DEPARTMENT_CONFIGURATION = gql`
  query GetDepartmentConfiguration($tenant: String!) {
    getDepartmentConfiguration(tenant: $tenant) {
      departments {
        id
        name
        description
        parentDepartment
        isActive
      }
      organizationalStructure {
        hierarchyLevels
        reportingStructure
      }
    }
  }
`;

export const GET_REGULATORY_AUTHORITY_TYPES = gql`
  query GetRegulatoryAuthorityTypes($tenant: String!) {
    getRegulatoryAuthorityTypes(tenant: $tenant) {
      authorityType
      name
      description
      jurisdictionLevel
      applicableIndustries
    }
  }
`;

export const GET_BUSINESS_PROCESS_TYPES = gql`
  query GetBusinessProcessTypes($tenant: String!) {
    getBusinessProcessTypes(tenant: $tenant) {
      processType
      name
      description
      category
      typicalDuration
      complexityLevel
    }
  }
`;